import * as React from 'react';
import { sidebarState, actions, SidebarOptions } from '../../state/src/sidebar-slice';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

export interface AllowedSidebarMap {
  [paneType: string]: React.ReactType;
}

export const useSidebar = () => {
  const dispatch = useDispatch();
  const state = useSelector(sidebarState, shallowEqual);
  const openSidebar = React.useCallback(
    (args: { sidebarType: string; payload?: unknown; sidebarOptions?: SidebarOptions }) => {
      dispatch(actions.openSidebar(args));
    },
    [dispatch]
  );

  const closeSidebar = React.useCallback(() => {
    dispatch(actions.closeSidebar());
  }, [dispatch]);

  const allowlistSidebars = React.useCallback(
    (allowedSidebars?: AllowedSidebarMap) => {
      dispatch(actions.allowlistSidebars(allowedSidebars));
    },
    [dispatch]
  );

  /**
   * Call this method multiple times to store multiple sidebars in store
   * Useful when we wanted to club common sidebars and specific page sidebars on single page load
   */
  const addToAllowlistSidebars = React.useCallback(
    (allowedSidebars?: AllowedSidebarMap) => {
      dispatch(actions.addToAllowlistSidebars(allowedSidebars));
    },
    [dispatch]
  );

  /**
   * Clear specific page sidebars when unmount that page and it will keep common sidebars in stores.
   */
  const clearSidebars = React.useCallback(
    (allowedSidebars?: AllowedSidebarMap) => {
      dispatch(actions.clearSidebars(allowedSidebars));
    },
    [dispatch]
  );

  const updateSidebarOptions = React.useCallback(
    (options?: SidebarOptions) => {
      dispatch(actions.updateSidebarOptions(options));
    },
    [dispatch]
  );

  /**
   * Re-opens the sidebar stored in state.
   * Useful when the sidebar needs to be temporarily closed, such as when refetching data
   * TODO:: persist the form state
   */
  const reopenMostRecentSidebar = React.useCallback(
    (args?: { payload?: unknown }) => {
      if (state.sidebarType) {
        openSidebar({
          sidebarType: state.sidebarType,
          payload: args?.payload ?? state.payload,
          sidebarOptions: state.sidebarOptions,
        });
      }
    },
    [openSidebar, state?.sidebarType, state?.payload, state?.sidebarOptions]
  );

  return {
    ...state,
    clearSidebars,
    addToAllowlistSidebars,
    allowlistSidebars,
    closeSidebar,
    openSidebar,
    updateSidebarOptions,
    reopenMostRecentSidebar,
  };
};
